<script setup lang="ts">
/**
 * The history sidebar.
 *
 * History is additive: if it fails to load, this shows an error row and the chat
 * keeps working. A conversation-store outage must degrade to "no sidebar", never
 * to "no chat".
 */
import { onMounted, ref } from 'vue';
import { Button } from '@asabdev/asab-ui';

import { conversationsApi, type Conversation, type ConversationKind } from '../api/conversations';

const props = defineProps<{
  kind: ConversationKind;
  agentId?: string;
  activeId?: string;
  /** Offers the "don't save this" toggle on the new-conversation control. */
  allowEphemeral?: boolean;
  /** Surface-supplied copy for that toggle. Not defaulted: the guarantees
   *  genuinely differ per surface, and a shared default would be wrong for one. */
  ephemeralNotice?: string;
}>();

const emit = defineEmits<{
  select: [id: string];
  create: [ephemeral: boolean];
  deleted: [id: string];
}>();

const conversations = ref<Conversation[]>([]);
const cursor = ref<string | undefined>();
const loading = ref(false);
const error = ref<string | null>(null);
const renamingId = ref<string | null>(null);
const renameText = ref('');
const ephemeral = ref(false);

async function load(more = false) {
  if (loading.value) return;
  loading.value = true;
  error.value = null;
  try {
    const page = await conversationsApi.list({
      kind: props.kind,
      agent_id: props.agentId,
      cursor: more ? cursor.value : undefined,
    });
    conversations.value = more ? [...conversations.value, ...page.conversations] : page.conversations;
    cursor.value = page.nextCursor;
  } catch {
    error.value = 'Could not load history.';
  } finally {
    loading.value = false;
  }
}

function startRename(c: Conversation) {
  renamingId.value = c.id;
  renameText.value = c.title;
}

async function commitRename(c: Conversation) {
  const title = renameText.value.trim();
  renamingId.value = null;
  if (!title || title === c.title) return;
  const previous = c.title;
  c.title = title; // optimistic — a rename that lags feels broken
  try {
    await conversationsApi.update(c.id, { title });
  } catch {
    c.title = previous;
    error.value = 'Could not rename.';
  }
}

async function remove(c: Conversation) {
  // Confirm: history is durable now, and a mis-click is not recoverable by the
  // user — the purge grace period is an operator affordance, not an undo.
  if (!window.confirm(`Delete "${c.title || 'this conversation'}"? This cannot be undone.`)) return;
  try {
    await conversationsApi.remove(c.id);
    conversations.value = conversations.value.filter((x) => x.id !== c.id);
    emit('deleted', c.id);
  } catch {
    error.value = 'Could not delete.';
  }
}

onMounted(load);
defineExpose({ reload: () => load(false) });
</script>

<template>
  <aside
    data-testid="conversation-list"
    class="flex w-64 shrink-0 flex-col border-r border-gray-200 dark:border-gray-700"
  >
    <div class="space-y-2 border-b border-gray-200 p-3 dark:border-gray-700">
      <Button size="sm" data-testid="new-conversation" class="w-full" @click="emit('create', ephemeral)">
        New chat
      </Button>
      <!-- Create-time only: the flag is immutable once set, so showing it on an
           existing conversation would read as a control that silently does nothing. -->
      <label v-if="allowEphemeral" class="flex items-start gap-1.5 text-xs text-gray-500">
        <input v-model="ephemeral" data-testid="ephemeral-toggle" type="checkbox" class="mt-0.5 rounded" />
        <span>
          Don't save this conversation
          <span v-if="ephemeralNotice" class="block text-[10px] text-gray-400">{{ ephemeralNotice }}</span>
        </span>
      </label>
    </div>

    <div class="flex-1 overflow-y-auto p-2">
      <p v-if="error" data-testid="history-error" class="px-2 py-1 text-xs text-red-500">
        {{ error }}
      </p>
      <p
        v-else-if="!conversations.length && !loading"
        class="px-2 py-1 text-xs text-gray-400"
      >
        No conversations yet.
      </p>

      <ul class="space-y-0.5">
        <li v-for="c in conversations" :key="c.id">
          <div
            class="group flex items-center gap-1 rounded-lg px-2 py-1.5"
            :class="
              c.id === activeId
                ? 'bg-gray-100 dark:bg-gray-800'
                : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'
            "
          >
            <input
              v-if="renamingId === c.id"
              v-model="renameText"
              data-testid="rename-input"
              class="min-w-0 flex-1 rounded border border-gray-300 bg-white px-1 text-sm dark:border-gray-600 dark:bg-gray-900"
              @keydown.enter="commitRename(c)"
              @keydown.esc="renamingId = null"
              @blur="commitRename(c)"
            />
            <button
              v-else
              type="button"
              class="min-w-0 flex-1 truncate text-left text-sm"
              @click="emit('select', c.id)"
              @dblclick="startRename(c)"
            >
              {{ c.title || 'Untitled' }}
              <span v-if="c.ephemeral" class="ml-1 text-[10px] text-gray-400">unsaved</span>
            </button>
            <button
              type="button"
              :aria-label="`Delete ${c.title || 'conversation'}`"
              data-testid="delete-conversation"
              class="opacity-0 transition-opacity group-hover:opacity-100"
              @click="remove(c)"
            >
              🗑
            </button>
          </div>
        </li>
      </ul>

      <button
        v-if="cursor"
        type="button"
        data-testid="load-more"
        class="mt-2 w-full px-2 py-1 text-xs text-gray-500 hover:text-gray-700"
        :disabled="loading"
        @click="load(true)"
      >
        Load more
      </button>
    </div>
  </aside>
</template>
